import { useEffect, useRef, useState } from 'react';

import VectorIcon from 'assets/VectorIcon.svg';
import UploadIcon from 'assets/settings/components/UploadIcon';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import * as Yup from 'yup';

import {
  ErrorText,
  ErrorText2,
  ErrorText3,
  ImagePreview,
  ImagePreviewContainer,
  ModalFormContent,
  RequiredAsterisk,
  SwitchWrapper,
  UploadButton,
  UploadContainer,
  UploadHiddenInput,
  UploadIconWrapper,
  UploadLabel,
  UploadText,
} from '../LeadSourceModal/LeadSourceModal.styles';

const productSchema = Yup.object({
  name: Yup.string().trim().required('Name is required').max(50),

  description: Yup.string().max(250),

  image: Yup.mixed()
    .nullable()
    .test('required', 'Image is required', function (file) {
      const { isEditMode } = this.options.context;

      if (!isEditMode && !file) {
        return false;
      }

      return true;
    })
    .test('fileType', 'Only image files are allowed', (file) => {
      if (!file) return true;

      return file.type?.startsWith('image/');
    })
    .test('fileSize', 'Image must be less than 5MB', (file) => {
      if (!file) return true;

      return file.size <= 5 * 1024 * 1024;
    }),

  enabled: Yup.boolean(),
});

const ErrorWithIcon = ({ message }) => {
  if (!message) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <img src={VectorIcon} alt="error" width={14} height={14} />
      <span>{message}</span>
    </div>
  );
};

const ProductModal = ({
  mode = 'create',
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  initialData,
}) => {
  const isEditMode = mode === 'edit';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
    enabled: true,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);

  const hasImage = !!imagePreview;
  const isUploadDisabled = !!imagePreview || !!formData.image;

  useEffect(() => {
    if (isOpen && isEditMode && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        image: null,
        enabled: initialData.enabled ?? true,
      });

      const baseUrl = process.env.REACT_APP_SALES_API_URL || '';

      const url = initialData.image
        ? initialData.image.startsWith('http')
          ? initialData.image
          : `${baseUrl}${initialData.image}`
        : null;

      setImagePreview(url);
    }

    if (isOpen && !isEditMode) {
      setFormData({
        name: '',
        description: '',
        image: null,
        enabled: true,
      });

      setImagePreview(null);
    }

    setErrors({});
  }, [isOpen, isEditMode, initialData]);

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      image: null,
      enabled: true,
    });

    setImagePreview(null);

    setErrors({});

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onClose();
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleSwitchToggle = () => {
    setFormData((prev) => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      image: file,
    }));

    const reader = new FileReader();

    reader.onloadend = () => {
      setImagePreview(reader.result);
    };

    reader.readAsDataURL(file);

    setErrors((prev) => ({
      ...prev,
      image: '',
    }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));

    setImagePreview(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    try {
      setErrors({});

      await productSchema.validate(formData, {
        abortEarly: false,
        context: { isEditMode },
      });

      const fd = new FormData();

      fd.append('name', formData.name.trim());
      fd.append('description', formData.description.trim());
      fd.append('enabled', String(formData.enabled));

      if (formData.image) {
        fd.append('image', formData.image);
      }

      if (isEditMode) {
        await onUpdate(initialData.id, fd);
      } else {
        await onCreate(fd);
      }

      handleClose();
    } catch (err) {
      const newErrors = {};

      if (err.inner) {
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
      }

      setErrors(newErrors);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit Product' : 'Create Product'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <ModalFormContent>
        <div style={{ position: 'relative' }}>
          <Input
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="Enter product name"
            required
            maxLength={50}
            error={!!errors.name}
          />
          {errors.name && (
            <ErrorText>
              <ErrorWithIcon message={errors.name} />
            </ErrorText>
          )}
        </div>

        <TextArea
          label="Description (Optional)"
          value={formData.description}
          onChange={handleInputChange('description')}
          placeholder="Enter description"
          rows={3}
          maxLength={250}
        />
        <div style={{ position: 'relative' }}>
          <UploadContainer>
            <UploadLabel>
              Image {!isEditMode && <RequiredAsterisk>*</RequiredAsterisk>}
            </UploadLabel>

            <UploadHiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />

            <UploadButton
              type="button"
              onClick={handleUploadClick}
              disabled={isUploadDisabled}
              style={{
                opacity: isUploadDisabled ? 0.5 : 1,
                cursor: isUploadDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <UploadIconWrapper>
                <UploadIcon size={32} color="#296BCD" />
              </UploadIconWrapper>

              <UploadText>{hasImage ? 'Image uploaded' : 'Click to upload image'}</UploadText>
            </UploadButton>

            {/* IMAGE PREVIEW */}
            {hasImage && (
              <ImagePreviewContainer
                style={{
                  position: 'relative',
                  marginTop: 12,
                  background: 'transparent',
                  padding: 0,
                  border: 'none',
                  width: 120,
                  height: 80,
                }}
              >
                <ImagePreview
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />

                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: '#fff',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: '#000',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </ImagePreviewContainer>
            )}

            {errors.image && (
              <ErrorText3>
                <ErrorWithIcon message={errors.image} />
              </ErrorText3>
            )}
          </UploadContainer>
        </div>
        <SwitchWrapper>
          <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} />

          {formData.enabled ? 'Enabled' : 'Disabled'}
        </SwitchWrapper>
      </ModalFormContent>
    </Modal>
  );
};

export default ProductModal;
